// Smart Personalization Engine for SheSafe Haven

export class PersonalizationEngine {
  constructor() {
    this.storageKey = 'shesafe_personalization';
    this.data = this.loadData();
  }

  loadData() {
    const stored = localStorage.getItem(this.storageKey);
    if (stored) {
      return JSON.parse(stored);
    }
    
    return {
      category_views: {
        healing: 0,
        strength: 0,
        sisterhood: 0,
        empowerment: 0
      },
      category_favorites: {
        healing: 0,
        strength: 0,
        sisterhood: 0,
        empowerment: 0
      },
      journal_times: [],
      active_days: [],
      last_active: null,
      streak: 0,
      preferred_notification_time: '9:00',
      total_quotes_viewed: 0,
      total_journal_entries: 0,
      milestones_achieved: 0
    };
  }

  saveData() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.data));
  }

  // Track quote view
  trackQuoteView(category) {
    if (this.data.category_views[category] !== undefined) {
      this.data.category_views[category]++;
      this.data.total_quotes_viewed++;
      this.saveData();
    }
  }

  // Track favorite
  trackFavorite(category) {
    if (this.data.category_favorites[category] !== undefined) {
      this.data.category_favorites[category]++;
      this.saveData();
    }
  }

  // Track journal entry
  trackJournalEntry() {
    const now = new Date();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const timeStr = `${hour}:${minute}`;
    
    this.data.journal_times.push({
      time: timeStr,
      hour: hour,
      timestamp: now.toISOString()
    });
    
    this.data.total_journal_entries++;
    
    // Keep only last 30 journal times
    if (this.data.journal_times.length > 30) {
      this.data.journal_times = this.data.journal_times.slice(-30);
    }
    
    this.saveData();
  }

  // Track daily activity
  trackDailyActivity() {
    const today = new Date().toISOString().split('T')[0];
    const yesterday = this.data.last_active;
    
    // Update streak
    if (yesterday) {
      const yesterdayDate = new Date(yesterday);
      const todayDate = new Date(today);
      const diffDays = Math.floor((todayDate - yesterdayDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        this.data.streak++;
      } else if (diffDays > 1) {
        this.data.streak = 1;
      }
    } else {
      this.data.streak = 1;
    }
    
    this.data.last_active = today;
    
    if (!this.data.active_days.includes(today)) {
      this.data.active_days.push(today);
      // Keep only last 90 days
      if (this.data.active_days.length > 90) {
        this.data.active_days = this.data.active_days.slice(-90);
      }
    }
    
    this.saveData();
  }

  // Get preferred categories (sorted by views + favorites)
  getPreferredCategories() {
    const scores = Object.keys(this.data.category_views).map(category => ({
      category,
      score: (this.data.category_views[category] * 2) + (this.data.category_favorites[category] * 3)
    }));
    
    return scores
      .sort((a, b) => b.score - a.score)
      .map(item => item.category);
  }

  // Get optimal notification time based on journal activity
  getOptimalNotificationTime() {
    if (this.data.journal_times.length < 5) {
      return this.data.preferred_notification_time;
    }
    
    // Find most common hour for journaling
    const hourCounts = {};
    this.data.journal_times.forEach(entry => {
      const hour = entry.hour;
      hourCounts[hour] = (hourCounts[hour] || 0) + 1;
    });
    
    // Get hour with most activity
    let maxHour = 9; // default
    let maxCount = 0;
    Object.entries(hourCounts).forEach(([hour, count]) => {
      if (count > maxCount) {
        maxCount = count;
        maxHour = parseInt(hour);
      }
    });
    
    // Suggest notification 1 hour before most active time
    const optimalHour = maxHour > 0 ? maxHour - 1 : 9;
    return `${optimalHour}:00`;
  }

  // Get affirming message based on activity
  getAffirmingMessage() {
    const messages = [];
    
    // Streak-based messages
    if (this.data.streak >= 7) {
      messages.push(`You've been showing up for yourself beautifully this week. ${this.data.streak} days strong. 💜`);
    } else if (this.data.streak >= 3) {
      messages.push(`${this.data.streak} days of choosing yourself. You're doing amazing. ✨`);
    }
    
    // Journal-based messages
    if (this.data.total_journal_entries >= 10) {
      messages.push(`${this.data.total_journal_entries} moments of reflection. Your inner voice is getting clearer. 📖`);
    }
    
    // Category preference messages
    const preferred = this.getPreferredCategories()[0];
    if (preferred && this.data.category_views[preferred] > 10) {
      const categoryMessages = {
        healing: "You're walking your healing path with courage.",
        strength: "Your strength is building beautifully, one day at a time.",
        sisterhood: "You're nurturing connection and support.",
        empowerment: "You're reclaiming your power steadily."
      };
      messages.push(categoryMessages[preferred]);
    }
    
    // Default encouraging message
    if (messages.length === 0) {
      messages.push("You're exactly where you need to be. Keep going. 💜");
    }
    
    return messages[Math.floor(Math.random() * messages.length)];
  }

  // Get personalized insights
  getInsights() {
    return {
      streak: this.data.streak,
      total_days: this.data.active_days.length,
      preferred_categories: this.getPreferredCategories(),
      optimal_notification_time: this.getOptimalNotificationTime(),
      affirming_message: this.getAffirmingMessage(),
      total_quotes_viewed: this.data.total_quotes_viewed,
      total_journal_entries: this.data.total_journal_entries,
      most_journaling_hour: this.getMostActiveJournalHour()
    };
  }

  getMostActiveJournalHour() {
    if (this.data.journal_times.length === 0) return null;
    
    const hourCounts = {};
    this.data.journal_times.forEach(entry => {
      hourCounts[entry.hour] = (hourCounts[entry.hour] || 0) + 1;
    });
    
    let maxHour = null;
    let maxCount = 0;
    Object.entries(hourCounts).forEach(([hour, count]) => {
      if (count > maxCount) {
        maxCount = count;
        maxHour = parseInt(hour);
      }
    });
    
    return maxHour;
  }

  // Update milestone achievement count
  updateMilestones(count) {
    this.data.milestones_achieved = count;
    this.saveData();
  }
}

// Export singleton instance
export const personalization = new PersonalizationEngine();
